import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { QRCodeCanvas } from "qrcode.react";
import { CheckCircle2, Clock, ExternalLink, Loader2, X } from "lucide-react";
import HeartBurst from "./HeartBurst";
import { getDonation, formatBRL, simulateDonation } from "@/lib/api";

const DEMO_PIX = (donationId) => `PETLOVERS|PIX-DEMO|${donationId}`;

export default function PixModal({ donation, onClose }) {
  const [phase, setPhase] = useState("loading");
  const [confirmed, setConfirmed] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(900);

  useEffect(() => {
    const t = setTimeout(() => setPhase("pix"), 900);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onMsg = (e) => {
      if (e?.data?.type === "close-pix-modal") onClose();
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [onClose]);

  useEffect(() => {
    if (phase !== "pix") return;
    const iv = setInterval(async () => {
      try {
        const d = await getDonation(donation.donation_id);
        if (d.status === "received") {
          if (donation.demo) setPhase("success");
          else setConfirmed(true);
        }
      } catch (e) {
        /* segue tentando */
      }
    }, 4000);
    const tick = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => {
      clearInterval(iv);
      clearInterval(tick);
    };
  }, [phase, donation.donation_id, donation.demo]);

  const qrValue = donation.demo ? DEMO_PIX(donation.donation_id) : donation.checkout_url;
  const minutes = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const seconds = String(secondsLeft % 60).padStart(2, "0");

  const simulate = async () => {
    try {
      await simulateDonation(donation.donation_id);
      setPhase("success");
    } catch (e) {
      /* noop */
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-[#261C14]/70 p-4 backdrop-blur-sm"
      data-testid="pix-modal-container"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
        className="relative max-h-[92vh] w-full max-w-md overflow-y-auto rounded-[1.75rem] bg-white p-6 text-center shadow-2xl sm:p-7"
      >
        <button
          onClick={onClose}
          data-testid="pix-modal-close-btn"
          className="absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-full bg-[#F7F3ED] text-[#6E6259] transition hover:bg-[#FFE2D9]"
        >
          <X className="h-4 w-4" />
        </button>

        {phase === "loading" && (
          <div className="py-14">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-[#FF6D47]" />
            <p className="font-display mt-6 text-xl font-extrabold text-[#261C14]">Gerando o seu Pix...</p>
            <p className="mt-2 text-sm font-semibold text-[#6E6259]">Conectando com segurança à LivePix</p>
          </div>
        )}

        {phase === "pix" && (
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#9E9085]">Doação de</p>
            <p className="font-display text-4xl font-extrabold text-[#FF6D47]">{formatBRL(donation.amount_cents)}</p>
            <p className="mt-1 flex items-center justify-center gap-1.5 text-xs font-bold text-[#9E9085]">
              <Clock className="h-3.5 w-3.5" /> O código expira em {minutes}:{seconds}
            </p>

            {donation.demo ? (
              <div className="mt-4">
                <div className="mx-auto w-fit rounded-2xl border-2 border-[#EFE9DF] bg-white p-3 shadow-sm" data-testid="pix-qrcode-image">
                  <QRCodeCanvas value={qrValue} size={180} level="M" bgColor="#FFFFFF" fgColor="#261C14" />
                </div>
                <p className="mt-3 text-sm font-bold text-[#261C14]">QR Code de teste (modo demonstração)</p>
                <button
                  onClick={simulate}
                  data-testid="pix-simulate-confirm-btn"
                  className="mt-4 flex h-12 w-full items-center justify-center rounded-full bg-[#059669] text-sm font-extrabold text-white transition hover:bg-[#047857]"
                >
                  Simular confirmação do Pix (modo teste)
                </button>
              </div>
            ) : (
              <div className="mt-4" data-testid="pix-iframe-container">
                <iframe
                  src={donation.checkout_url}
                  title="Checkout Pix da LivePix"
                  data-testid="pix-iframe"
                  className="h-[460px] w-full rounded-2xl border-2 border-[#EFE9DF] bg-white"
                  allow="clipboard-write"
                />
                <a
                  href={donation.checkout_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="pix-open-checkout-btn"
                  className="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-full border-2 border-[#32BCAD]/40 text-sm font-extrabold text-[#0B8B7E] transition hover:bg-[#32BCAD]/10"
                >
                  <ExternalLink className="h-4 w-4" /> Não carregou? Abrir o checkout em nova aba
                </a>
              </div>
            )}

            {confirmed ? (
              <div
                data-testid="pix-confirmed-note"
                className="mt-3 flex items-center justify-center gap-2 rounded-xl bg-[#E8F5EE] px-4 py-3 text-sm font-extrabold text-[#047857]"
              >
                <CheckCircle2 className="h-4 w-4" /> Pagamento confirmado! Pode fechar quando quiser.
              </div>
            ) : (
              <p className="mt-3 text-[11px] leading-relaxed text-[#9E9085]">
                Pague com Pix no checkout acima — escaneie o QR Code ou use o Pix Copia e Cola. Assim que o pagamento for
                confirmado, esta janela celebra com você automaticamente.
              </p>
            )}
          </div>
        )}

        {phase === "success" && (
          <div className="relative py-6">
            <HeartBurst />
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 16 }}>
              <CheckCircle2 className="mx-auto h-16 w-16 text-[#059669]" />
            </motion.div>
            <h3 data-testid="pix-success-message" className="font-display mt-4 text-2xl font-extrabold text-[#261C14]">
              Doação confirmada!
            </h3>
            <p className="mt-2 text-sm font-semibold text-[#6E6259]">
              Obrigado, {donation.donorName}! Sua doação de{" "}
              <span className="font-extrabold text-[#FF6D47]">{formatBRL(donation.amount_cents)}</span> já está ajudando as
              patinhas do abrigo.
            </p>
            <button
              onClick={onClose}
              data-testid="pix-success-close-btn"
              className="mt-6 h-12 w-full rounded-full bg-[#FF6D47] font-extrabold text-white transition hover:bg-[#E85732]"
            >
              Fechar
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
